import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule } from '@angular/forms';
import { DefaultIcon, SDate } from '@summit/core';
import {
  DateRange,
  DateRangeType,
  DayOfWeek,
  IUserExperience,
  SummitDisposing,
  SummitUserExperienceService,
  Week
} from '@summit/models';
import { LayoutService } from '@summit/ui';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';

@Component({
  selector: 's-form-input-week',
  templateUrl: './s-form-input-week.component.html',
  styleUrls: ['./s-form-input-week.component.css'],
  standalone: true,
  imports: [FormsModule, FloatLabel, Button, DatePicker, AsyncPipe, InputGroup, InputGroupAddon],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputWeekComponent extends SummitDisposing implements OnInit {
  @Input() labelLocation: 'over' | 'in' | 'on' = 'over';
  @Input() label: string | undefined;
  @Input() size: 'small' | 'large';
  @Input() required: boolean;
  @Input() defaultWeekOffset = 0;
  @Input() defaultRange: DateRange;
  @Input() defaultRangeType: DateRangeType = DateRangeType.TenantWeek;
  @ViewChild('calendar') calendar: DatePicker;
  @Output()
  DateRangeChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();
  private dateRangeFocus = false;
  private newWeekEnd: DayOfWeek;
  private currentDateRange: DateRange | null = null;
  public fieldName = 'weekRange';
  public controlValue?: Date;
  public rangeValue: DateRange;
  private calendarVisible = false;
  defaultIcon = DefaultIcon;

  constructor(
    fb: FormBuilder,
    private uxService: SummitUserExperienceService<IUserExperience>,
    protected layoutService: LayoutService
  ) {
    super();
  }

  ngOnInit() {
    // console.log(
    //   `[Week Picker] Initializing with ${this.defaultRange?.begin?.format('MM/DD')} & ${this.defaultRange?.end?.format(
    //     'MM/DD'
    //   )}`,
    //   this.defaultRange,
    //   this.defaultRangeType
    // );
    this.defaultRangeType = this.defaultRangeType ?? DateRangeType.TenantWeek;
    if (this.defaultRange) {
      //console.log(`[RangePicker] Initializing with ${this.defaultRange?.begin?.format('MM/DD')}`);
      this.updateRange(this.defaultRange);
    } else {
      const selWeek = new Week(this.defaultWeekOffset, this.defaultRangeType);
      this.updateRange(selWeek.ToDateRange(this.defaultRangeType));
    }
    this.updateRange;
  }

  public onShow() {
    this.calendarVisible = true;
  }
  public onClose() {
    this.calendarVisible = false;
  }

  public ProcessRangeChange() {
    const startWeek = new Week(this.rangeValue.begin, this.defaultRangeType);
    const endWeek = new Week(this.rangeValue.end ?? this.rangeValue.begin, this.defaultRangeType);
    const startRange = startWeek.ToDateRange(this.defaultRangeType);
    const endRange = endWeek.ToDateRange(this.defaultRangeType);
    this.updateRange(new DateRange({ begin: startRange.begin, end: endRange.end }));
  }

  private getDefaultDateRange(): DateRange {
    return this.defaultRange ?? new Week(0, this.defaultRangeType).ToDateRange(this.defaultRangeType);
  }

  updateRange(newRange?: DateRange) {
    newRange = newRange ? newRange : this.getDefaultDateRange();
    // console.log(
    //   `[RangePicker] Updating Range: ${newRange.begin.format('MM/DD/YYYY')} - ${newRange.end.format('MM/DD/YYYY')}`
    // );
    const ux = this.uxService.getUserExperience();
    let targetEndDayOfWeek = ux.activeEmployee.endOfWeek;
    // console.log('type', this.defaultRangeType);
    switch (this.defaultRangeType) {
      case DateRangeType.SettlementWeek: {
        // console.log('Settlement Week');
        targetEndDayOfWeek = DayOfWeek.Friday;
        break;
      }
      case DateRangeType.ScheduleWeek: {
        // console.log('Schedule Week', ux.activeEmployee);
        targetEndDayOfWeek = ux.activeEmployee.endOfScheduleWeek;
        break;
      }
      case DateRangeType.IVMRWeek: {
        // console.log('IVMR Week');
        targetEndDayOfWeek = ux.activeEmployee.endOfIvmrWeek;
        break;
      }
      default: {
        //default above
        break;
      }
    }
    // console.log('targetEndDayOfWeek', targetEndDayOfWeek);
    if (newRange.end.day() != targetEndDayOfWeek) {
      // console.log(`[RangePicker] Adjusting End of Week to ${targetEndDayOfWeek}`);
      newRange.end = newRange.end.day(targetEndDayOfWeek).add(1, 'week');
    }
    newRange.begin = newRange.end.subtract(6, 'day');
    if (!this.rangeValue || !newRange.end.isSame(this.rangeValue.end)) {
      this.controlValue = SDate.ShiftTZToLocal(newRange.end) ?? newRange.end.toDate();
      this.rangeValue = newRange;
      // console.log(
      //   `[RangePicker] Updating Range: ${this.rangeValue.begin.format('MM/DD/YYYY')} - ${this.rangeValue.end.format(
      //     'MM/DD/YYYY'
      //   )}`
      // );
      this.DateRangeChange.emit(new DateRange(this.rangeValue));
    }
  }
  moveToToday() {
    const newRange = new DateRange(this.defaultRangeType);
    // console.log(`Moving to Today: ${newRange.begin.format()}-${newRange.end.format()}`);
    this.updateRange(newRange);
  }

  moveBackWeek() {
    // Enter key in input is triggering this so added a if to separate decision
    if (!this.dateRangeFocus) {
      const currentValue = new DateRange(this.rangeValue);
      currentValue.begin = currentValue.begin.add(-1, 'week');
      currentValue.end = currentValue.end.add(-1, 'week');
      this.updateRange(currentValue);
    } else {
      this.ProcessRangeChange();
    }
  }

  moveForwardWeek() {
    //console.log('Forward a week');
    const currentValue = new DateRange(this.rangeValue);
    currentValue.begin = currentValue.begin.add(1, 'week');
    currentValue.end = currentValue.end.add(1, 'week');
    this.updateRange(currentValue);
  }

  getRange(): DateRange {
    return new DateRange(this.rangeValue);
  }
  /*
  setFocusTracking(newFocusState: boolean) {
    if (!newFocusState) {
      //console.log(`Focus changed calling ProcessRange`);
      this.ProcessRangeChange();
    }
    this.dateRangeFocus = newFocusState;
  }
*/
  onChange() {
    // console.log(`${this.fieldName.toString()}: onChange`, this.controlValue);
    if (!this.controlValue) return;
    this.rangeValue = new DateRange({
      begin: SDate.newUtc(this.controlValue),
      end: SDate.newUtc(this.controlValue)
    });
    // console.log(`${this.fieldName.toString()}: onChange`, this.rangeValue);
    this.ProcessRangeChange();
    if (this.calendarVisible) {
      this.calendarVisible = false;
      this.calendar?.toggle();
    }
  }
}
